/**
 * Générateur — Automatisme : reconnaître une fraction sur des représentations variées
 * Attendu ID : 1010 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — réactivation d'acquis CM2.
 * Labels (cartes exerciseur) :
 *   - Reconnaître une fraction (bande, disque, jetons, droite graduée)
 *
 * ── Plan de cartes validé (2026-08-20) ─────────────────────────────────────
 * UNE carte, 5 formats. QUATRE supports, ceux de la série flash : la
 * DEMI-DROITE GRADUÉE (seul support prescrit par le programme de CM2,
 * sur-représentée : deux formats dédiés), la bande, le disque, les jetons
 * (choix pédagogiques standards). INVARIANTS :
 *   - chaque figure est un SVG inline (data-URL, convention imageUrl)
 *     CONSTRUIT depuis (a, n) — jamais l'inverse — et (a, n) est vérifié
 *     (1 ≤ a < n, throw sinon) : la figure ne peut pas contredire la clé ;
 *   - dénominateurs LISIBLES : n ∈ [2..12] (bande/disque/jetons),
 *     n ∈ [2..10] (droite graduée) — loin du plafond 60 du CM2, c'est de la
 *     reconnaissance visuelle ;
 *   - fractions NON simplifiées admises (2/8 lue telle quelle, comme la
 *     flash) ; JAMAIS a = n ;
 *   - saisie : UN seul trou, le dénominateur est PRÉ-REMPLI (arbitrage prof
 *     2026-08-20 : « sans simplifier » est du jargon en 6e — avec ?/8
 *     affiché, l'élève compte, l'ambiguïté 1/4 vs 2/8 disparaît) ; variante
 *     inverse (numérateur donné, total des parts à compter) 1 fois sur 3 ;
 *   - QCM : distracteurs NOMMÉS (inverse, complément, colorié/blanc,
 *     graduations comptées au lieu des intervalles sur la droite), vérifiés
 *     ≠ bonne en VALEUR (produit en croix) et en texte ;
 *   - V/F : la vérité d'une ligne-fraction est ÉVALUÉE NUMÉRIQUEMENT
 *     (produit en croix), pas par comparaison de paires.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1010;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Reconnaître une fraction (bande, disque, jetons, droite graduée)';

// ─── Couleurs (design system) ──────────────────────────────────────────────
const COLORIE = '#60a5fa';
const VIDE = '#ffffff';
// Traits de séparation des parts : NOIR unique et GRAS partout (arbitrage
// prof 2026-08-20 : mêmes traits pour parts coloriées et blanches).
const NOIR = '#111827';
const ROUGE = '#dc2626';

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

/** Garde-fou : la fraction lue doit être licite AVANT de dessiner. */
const verifierFraction = (a, n, nMax) => {
    if (!Number.isInteger(a) || !Number.isInteger(n)
        || a < 1 || n < 2 || a >= n || n > nMax) {
        throw new Error(`gen_1010 : fraction hors domaine (${a}/${n})`);
    }
};

// ─── Les quatre figures (construites depuis a et n uniquement) ─────────────

/**
 * Coloriage DISPERSÉ ~70 % du temps (arbitrage prof 2026-08-20 : le bloc
 * contigu se lit « à la longueur » sans compter les parts), contigu sinon.
 */
const indicesColories = (n, a) => (
    Math.random() < 0.7
        ? new Set(shuffle(Array.from({ length: n }, (_, k) => k)).slice(0, a))
        : new Set(Array.from({ length: a }, (_, k) => k))
);

const bandeSvg = (n, a) => {
    verifierFraction(a, n, 12);
    const w = 300 / n;
    const colories = indicesColories(n, a);
    // Variante « graduée » RETIRÉE (essais prof 2026-08-20) : entre deux
    // parts blanches voisines il ne restait que des ticks alors que les
    // parts coloriées gardaient leur contour plein — une zone blanche
    // double se lisait comme UNE grande part. Toutes les cases portent
    // le contour noir gras, toujours.
    const cases = Array.from({ length: n }, (_, k) => {
        const fill = colories.has(k) ? COLORIE : VIDE;
        return `<rect x="${(10 + k * w).toFixed(2)}" y="10" width="${w.toFixed(2)}" height="40" fill="${fill}" stroke="${NOIR}" stroke-width="2"/>`;
    }).join('');
    return dataUrl(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 60" width="320" role="img" aria-label="Une bande partagée en ${n} parts égales, ${a} coloriées">${cases}</svg>`);
};

const disqueSvg = (n, a) => {
    verifierFraction(a, n, 12);
    const cx = 70;
    const cy = 70;
    const r = 58;
    const pt = (k) => {
        const ang = (-90 + (360 * k) / n) * (Math.PI / 180);
        return `${(cx + r * Math.cos(ang)).toFixed(2)} ${(cy + r * Math.sin(ang)).toFixed(2)}`;
    };
    const colories = indicesColories(n, a);
    const secteurs = Array.from({ length: n }, (_, k) => {
        const fill = colories.has(k) ? COLORIE : VIDE;
        return `<path d="M${cx} ${cy} L${pt(k)} A ${r} ${r} 0 0 1 ${pt(k + 1)} Z" fill="${fill}" stroke="${NOIR}" stroke-width="2"/>`;
    }).join('');
    return dataUrl(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140 140" width="140" role="img" aria-label="Un disque partagé en ${n} parts égales, ${a} coloriées">${secteurs}</svg>`);
};

const jetonsSvg = (n, a) => {
    verifierFraction(a, n, 12);
    // Collection EN VRAC (arbitrage prof 2026-08-20 : des rangées alignées
    // sont la représentation la plus confortable qui soit) : les jetons
    // occupent des cellules TIRÉES AU HASARD d'une grille 6 × 3, avec un
    // petit décalage aléatoire — jamais de chevauchement (pas 36 > 2r + 6),
    // jamais d'alignement en rangées pleines.
    const cellules = shuffle(Array.from({ length: 18 }, (_, k) => k)).slice(0, n);
    const rouges = new Set(shuffle(Array.from({ length: n }, (_, k) => k)).slice(0, a));
    const jetons = cellules.map((cell, k) => {
        const cxJ = 24 + (cell % 6) * 36 + randInt(-5, 5);
        const cyJ = 24 + Math.floor(cell / 6) * 36 + randInt(-5, 5);
        const fill = rouges.has(k) ? ROUGE : '#e2e8f0';
        const stroke = rouges.has(k) ? '#7f1d1d' : '#64748b';
        return `<circle cx="${cxJ}" cy="${cyJ}" r="13" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`;
    }).join('');
    return dataUrl(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 234 126" width="234" role="img" aria-label="${n} jetons, dont ${a} rouges">${jetons}</svg>`);
};

const droiteSvg = (n, a) => {
    verifierFraction(a, n, 10);
    const x0 = 20;
    const pas = 300 / n;
    const ticks = Array.from({ length: n + 1 }, (_, k) => {
        const x = (x0 + k * pas).toFixed(2);
        const h = k === 0 || k === n ? 12 : 8;
        return `<line x1="${x}" y1="${40 - h}" x2="${x}" y2="${40 + h}" stroke="#334155" stroke-width="2"/>`;
    }).join('');
    const xA = (x0 + a * pas).toFixed(2);
    return dataUrl(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 74" width="340" role="img" aria-label="Une demi-droite graduée de 0 à 1 partagée en ${n} intervalles égaux, le point A est à la graduation ${a}">`
        + '<line x1="20" y1="40" x2="330" y2="40" stroke="#334155" stroke-width="2"/>'
        + ticks
        + `<text x="20" y="66" font-size="13" font-weight="700" fill="#334155" text-anchor="middle" font-family="system-ui,sans-serif">0</text>`
        + `<text x="320" y="66" font-size="13" font-weight="700" fill="#334155" text-anchor="middle" font-family="system-ui,sans-serif">1</text>`
        + `<circle cx="${xA}" cy="40" r="5" fill="${ROUGE}"/>`
        + `<text x="${xA}" y="20" font-size="14" font-weight="800" fill="${ROUGE}" text-anchor="middle" font-family="system-ui,sans-serif">A</text>`
        + '</svg>');
};

// ─── Tirages ───────────────────────────────────────────────────────────────

const SUPPORTS = ['bande', 'disque', 'jetons'];
const SVG_PAR_SUPPORT = { bande: bandeSvg, disque: disqueSvg, jetons: jetonsSvg };
const QUESTIONS = {
    bande: 'Quelle fraction de la bande est coloriée ?',
    disque: 'Quelle fraction du disque est coloriée ?',
    jetons: 'Quelle fraction des jetons est rouge ?',
};
const PHRASES_TROU = {
    bande: 'La fraction coloriée de la bande :',
    disque: 'La fraction coloriée du disque :',
    jetons: 'La fraction des jetons rouges :',
};

/**
 * Tirage UNIFORME sur les paires (a, n) — et non « n puis a » : sinon les
 * petits dénominateurs héritent de tout le poids du leur (1/2 sortait à
 * ~11 % sur la droite au lieu de ~2 %, signalement prof 2026-08-20).
 */
const PAIRES_PAR_MAX = {};
const tirerFraction = (nMax) => {
    if (!PAIRES_PAR_MAX[nMax]) {
        const paires = [];
        for (let n = 2; n <= nMax; n++) {
            for (let a = 1; a < n; a++) paires.push({ a, n });
        }
        PAIRES_PAR_MAX[nMax] = paires;
    }
    return randomChoice(PAIRES_PAR_MAX[nMax]);
};

const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const fracStr = (x, y) => `$\\dfrac{${x}}{${y}}$`;
const memeValeur = (x, y, a, n) => x * n === y * a;

/**
 * 3 distracteurs NOMMÉS pour la fraction a/n, tous ≠ bonne en valeur
 * (produit en croix) et en texte. `extra` ajoute les familles propres à la
 * droite graduée en tête de liste.
 */
const distracteursPour = (a, n, extra = []) => {
    const candidats = [
        ...extra,
        { x: n, y: a, code: 'inverse' },
        { x: n - a, y: n, code: 'complement' },
        { x: a, y: n - a, code: 'colorie_sur_blanc' },
        { x: a, y: n + 1, code: 'parts_mal_comptees' },
        { x: a, y: n + 2, code: 'parts_mal_comptees' },
    ];
    const pris = [];
    const vus = new Set([`${a}/${n}`]);
    for (const c of candidats) {
        if (c.x < 1 || c.y < 1) continue;
        if (memeValeur(c.x, c.y, a, n)) continue;
        if (c.x === c.y) continue;                       // \dfrac{a}{a} : illisible
        if (vus.has(`${c.x}/${c.y}`)) continue;
        vus.add(`${c.x}/${c.y}`);
        pris.push(c);
        if (pris.length === 3) break;
    }
    return pris;
};

// ─── Format 1 : QCM sur figure (bande / disque / jetons) ──────────────────

const genFigureQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const support = randomChoice(SUPPORTS);
        const { a, n } = tirerFraction(12);
        const dis = distracteursPour(a, n);
        if (dis.length < 3) continue;
        const choix = [fracStr(a, n), ...dis.map((c) => fracStr(c.x, c.y))];
        const texte = `<p><b>${QUESTIONS[support]}</b></p>{{element:0}}`;
        const payload = makeQcm(choix, choix[0], texte, {
            ...opts(L1), elements_additionnels: elImage(SVG_PAR_SUPPORT[support](n, a)),
        });
        payload.contenu = { ...payload.contenu, sousType: support };
        payload._meta.params = { support, a, n, distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(
        [fracStr(3, 4), fracStr(4, 3), fracStr(1, 4), fracStr(3, 5)],
        fracStr(3, 4),
        `<p><b>${QUESTIONS.bande}</b></p>{{element:0}}`,
        { ...opts(L1), elements_additionnels: elImage(bandeSvg(4, 3)) },
    );
    payload.contenu = { ...payload.contenu, sousType: 'bande' };
    payload._meta.params = { support: 'bande', a: 3, n: 4, distracteurs: [] };
    return payload;
};

// ─── Format 2 : saisie sur figure (dénominateur PRÉ-REMPLI) ───────────────

const genFigureSaisie = () => {
    const support = randomChoice(SUPPORTS);
    const { a, n } = tirerFraction(12);
    // 2 fois sur 3 le numérateur est à compter (dénominateur affiché) ;
    // sinon l'inverse (le total des parts est à compter).
    const trouNum = Math.random() < 2 / 3;
    const frac = trouNum ? `\\dfrac{{{${a}}}}{${n}}` : `\\dfrac{${a}}{{{${n}}}}`;
    // La phrase à trous est PUREMENT mathématique (convention 1005-1007 :
    // les lignes mixtes texte + $…$ ne sont pas rendues par toute la chaîne
    // corrigé/print) — l'intro vit dans l'énoncé HTML.
    return makeTexteTrou(
        `$${frac}$`,
        `<p><b>Complète la fraction en lisant la figure.</b></p>{{element:0}}<p>${PHRASES_TROU[support]}</p>`,
        {
            ...opts(L1), mode: 'saisie',
            elements_additionnels: elImage(SVG_PAR_SUPPORT[support](n, a)),
        },
    );
};

// ─── Format 3 : QCM sur la demi-droite graduée ────────────────────────────

const genDroiteQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const { a, n } = tirerFraction(10);
        // LE piège de la droite : compter les GRADUATIONS (n + 1) au lieu
        // des intervalles ; puis le point voisin, puis les familles communes.
        const extra = [
            { x: a, y: n + 1, code: 'graduations_au_lieu_des_intervalles' },
            a + 1 < n ? { x: a + 1, y: n, code: 'point_decale' } : { x: a - 1, y: n, code: 'point_decale' },
        ].filter((c) => c.x >= 1);
        const dis = distracteursPour(a, n, extra);
        if (dis.length < 3) continue;
        const choix = [fracStr(a, n), ...dis.map((c) => fracStr(c.x, c.y))];
        const texte = '<p><b>Quelle fraction est repérée par le point A ?</b></p>{{element:0}}';
        const payload = makeQcm(choix, choix[0], texte, {
            ...opts(L1), elements_additionnels: elImage(droiteSvg(n, a)),
        });
        payload.contenu = { ...payload.contenu, sousType: 'droite' };
        payload._meta.params = { support: 'droite', a, n, distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(
        [fracStr(2, 5), fracStr(2, 6), fracStr(3, 5), fracStr(5, 2)],
        fracStr(2, 5),
        '<p><b>Quelle fraction est repérée par le point A ?</b></p>{{element:0}}',
        { ...opts(L1), elements_additionnels: elImage(droiteSvg(5, 2)) },
    );
    payload.contenu = { ...payload.contenu, sousType: 'droite' };
    payload._meta.params = { support: 'droite', a: 2, n: 5, distracteurs: [] };
    return payload;
};

// ─── Format 4 : saisie sur la demi-droite graduée ─────────────────────────

const genDroiteSaisie = () => {
    const { a, n } = tirerFraction(10);
    const trouNum = Math.random() < 2 / 3;
    const frac = trouNum ? `\\dfrac{{{${a}}}}{${n}}` : `\\dfrac{${a}}{{{${n}}}}`;
    // Phrase purement mathématique (cf. genFigureSaisie), intro dans le texte.
    return makeTexteTrou(
        `$${frac}$`,
        '<p><b>Complète la fraction en lisant la demi-droite graduée.</b></p>{{element:0}}<p>Le point A repère la fraction :</p>',
        { ...opts(L1), mode: 'saisie', elements_additionnels: elImage(droiteSvg(n, a)) },
    );
};

// ─── Format 5 : V/F de lecture (une figure, 4 affirmations) ───────────────

const genLectureVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const support = randomChoice(SUPPORTS);
        const { a, n } = tirerFraction(12);
        const jeton = support === 'jetons';
        // Affirmation 1 : le partage (le total).
        const nAffiche = randomChoice([n, n, n + 1, n - 1].filter((x) => x >= 2));
        const l1 = jeton ? `Il y a ${nAffiche} jetons en tout` : `La figure est partagée en ${nAffiche} parts égales`;
        const v1 = nAffiche === n;
        // Affirmation 2 : le comptage (la partie).
        const aAffiche = randomChoice([a, a, a + 1, a - 1].filter((x) => x >= 1 && x < n + 2));
        // Accord singulier/pluriel (protection orthographique, signalement
        // prof 2026-08-20 : « 1 parts sont coloriées » est fautif).
        const l2 = jeton
            ? (aAffiche === 1 ? '1 jeton est rouge' : `${aAffiche} jetons sont rouges`)
            : (aAffiche === 1 ? '1 part est coloriée' : `${aAffiche} parts sont coloriées`);
        const v2 = aAffiche === a;
        // Affirmations 3 et 4 : deux fractions distinctes, vérité NUMÉRIQUE.
        const dis = distracteursPour(a, n);
        if (dis.length < 2) continue;
        const propositions = shuffle([{ x: a, y: n }, ...dis]).slice(0, 2);
        const intitule = jeton ? 'La fraction des jetons rouges est' : 'La fraction coloriée est';
        const l3 = `${intitule} ${fracStr(propositions[0].x, propositions[0].y)}`;
        const v3 = memeValeur(propositions[0].x, propositions[0].y, a, n);
        const l4 = `${intitule} ${fracStr(propositions[1].x, propositions[1].y)}`;
        const v4 = memeValeur(propositions[1].x, propositions[1].y, a, n);
        const lignes = [l1, l2, l3, l4];
        const verites = [v1, v2, v3, v4];
        const nbVraies = verites.filter(Boolean).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        if (new Set(lignes).size !== 4) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Observe la figure. Vrai ou faux ?</b></p>{{element:0}}',
            { ...opts(L1), elements_additionnels: elImage(SVG_PAR_SUPPORT[support](n, a)) },
        );
    }
    return makeVraiFaux(
        ['La figure est partagée en 4 parts égales', '3 parts sont coloriées',
            `La fraction coloriée est ${fracStr(3, 4)}`, `La fraction coloriée est ${fracStr(4, 3)}`],
        [0, 0, 0, 1],
        '<p><b>Observe la figure. Vrai ou faux ?</b></p>{{element:0}}',
        { ...opts(L1), elements_additionnels: elImage(bandeSvg(4, 3)) },
    );
};

// ─── Contrat distracteurs (référence de revue, formats QCM) ───────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est vérifié différent de la bonne réponse en VALEUR (produit en croix) et en TEXTE ; jamais de \\dfrac{a}{a}',
        'la figure est CONSTRUITE depuis (a, n) vérifiés — elle ne peut pas contredire la clé',
    ],
    reconnaitre_figure_qcm: {
        support: 'bande, disque ou jetons (contenu.sousType), n ∈ [2..12]',
        fixes: {
            inverse: 'numérateur et dénominateur échangés (8/3 pour 3/8)',
            complement: 'la partie NON coloriée (banni quand a/n = 1/2, il vaudrait la bonne)',
            colorie_sur_blanc: 'parts coloriées sur parts blanches (a/(n−a))',
            parts_mal_comptees: 'le total des parts surcompté (a/(n+1), a/(n+2))',
        },
    },
    reconnaitre_droite_qcm: {
        support: 'demi-droite graduée 0 → 1, n ∈ [2..10]',
        fixes: {
            graduations_au_lieu_des_intervalles: 'LE piège du support : compter les traits (n+1) au lieu des intervalles (a/(n+1))',
            point_decale: 'la graduation voisine ((a±1)/n)',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    reconnaitre_figure_qcm: genFigureQcm,
    reconnaitre_figure_saisie: genFigureSaisie,
    reconnaitre_droite_qcm: genDroiteQcm,
    reconnaitre_droite_saisie: genDroiteSaisie,
    reconnaitre_tableau_vf: genLectureVraiFaux,
};

const FORMAT_LABELS = {
    figure_qcm: 'QCM figure', figure_saisie: 'Saisie figure',
    droite_qcm: 'QCM droite', droite_saisie: 'Saisie droite',
    tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'reconnaitre', label: L1, formatsList: buildFormats('reconnaitre') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
